import React from "react";
import { Link } from "react-router-dom";
import { Anchor, Box, Group, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconArrowLeft, IconCheck } from "@tabler/icons-react";
import { Logo } from "./Logo";
import { BRAND_DARK, BRAND_GRADIENT } from "../theme/brand";

type AuthLayoutProps = {
  /** Product name shown beside the logo, e.g. "HMH Procurement". */
  product: string;
  /** Brand panel content (hidden on small screens). */
  panelEyebrow: string;
  panelTitle: string;
  panelPoints: string[];
  /** Form side. */
  title: string;
  subtitle?: string;
  /** Optional status icon shown above the title (e.g. on confirmation screens). */
  icon?: React.ReactNode;
  back: { label: string; to: string };
  children: React.ReactNode;
};

/** Split-screen layout shared by the staff and vendor sign-in pages. */
export function AuthLayout({
  product,
  panelEyebrow,
  panelTitle,
  panelPoints,
  title,
  subtitle,
  icon,
  back,
  children,
}: AuthLayoutProps) {
  return (
    <div className="grid min-h-screen bg-[var(--mantine-color-body)] lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      {/* Brand panel */}
      <Box
        visibleFrom="lg"
        style={{ background: BRAND_GRADIENT }}
        px={56}
        py={48}
        className="flex flex-col justify-between"
      >
        <Group gap="sm">
          <Box bg="white" px="sm" py={6} style={{ borderRadius: 8 }}>
            <Logo height={36} />
          </Box>
          <Text fw={700} size="lg" c="white">
            {product}
          </Text>
        </Group>

        <Stack gap="lg" maw={440}>
          <Text size="sm" fw={600} c="rgba(255,255,255,0.85)" tt="uppercase" lts={1}>
            {panelEyebrow}
          </Text>
          <Title order={2} c="white" fz={36} lh={1.2}>
            {panelTitle}
          </Title>
          <Stack gap="sm" component="ul" m={0} p={0} style={{ listStyle: "none" }}>
            {panelPoints.map((point) => (
              <Group key={point} gap="sm" wrap="nowrap" align="flex-start" component="li">
                <ThemeIcon size={22} radius="xl" color="white" c={BRAND_DARK} mt={1}>
                  <IconCheck size={14} aria-hidden />
                </ThemeIcon>
                <Text c="rgba(255,255,255,0.92)">{point}</Text>
              </Group>
            ))}
          </Stack>
        </Stack>

        <Text size="xs" c="rgba(255,255,255,0.7)">
          © {new Date().getFullYear()} Hulhumalé Hospital
        </Text>
      </Box>

      {/* Form side */}
      <main className="flex flex-col px-4 py-6 sm:px-8 lg:px-16">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" hiddenFrom="lg" wrap="nowrap">
            <Logo height={36} />
            <Text fw={700} c={BRAND_DARK} visibleFrom="sm">
              {product}
            </Text>
          </Group>
          <Anchor component={Link} to={back.to} size="sm" c="dimmed" ml="auto" style={{ whiteSpace: "nowrap" }}>
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={14} aria-hidden />
              {back.label}
            </Group>
          </Anchor>
        </Group>

        <div className="flex flex-1 items-start justify-center pt-10 pb-10 lg:items-center lg:pt-10">
          <Stack gap="lg" w="100%" maw={420}>
            <Stack gap={6}>
              {icon && <Box mb="xs">{icon}</Box>}
              <Title order={1} fz={30}>
                {title}
              </Title>
              {subtitle && <Text c="dimmed">{subtitle}</Text>}
            </Stack>
            {children}
          </Stack>
        </div>
      </main>
    </div>
  );
}
